import { Grid, Button } from "@material-ui/core";
import React from "react";
import { EventNotification } from "../../domain/EventNotification";
import { Controller, useFieldArray, useWatch } from "react-hook-form";
import { SectionProps } from "./SectionProps";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { centreNumberTypeService } from "../../services";
import { Select } from "@ofqual/portal-components/components/Select";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Error } from "../common/Error";
import { fieldLabels } from "./fieldLabels";

export const AffectedCentres: React.FC<SectionProps<EventNotification>> = ({
  control,
  disabled,
  errors,
  isCreateMode,
  getValues,
  lookupTracker,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "eventNotificationCentres",
    keyName: "_id",
  });

  const currentResults = useWatch({
    control,
    name: "eventNotificationCentres",
  }) as any;

  const getValuesForIndex = (index: number) => currentResults[index];

  const [centreNumberTypeLookups] = useLookup(centreNumberTypeService);

  const NATYPEID = centreNumberTypeLookups.find((x) => x.label === "N/A")?.id;
  const NCNTYPEID = centreNumberTypeLookups.find((x) => x.label === "NCN")?.id;
  const UKPRNTYPEID = centreNumberTypeLookups.find((x) => x.label === "UKPRN")
    ?.id;

  return (
    <Grid container spacing={3}>
      {fields.map((item, index) => {
        const fieldNames = {
          centreNumberType: `eventNotificationCentres[${index}].centreNumberType`,
          centreNumber: `eventNotificationCentres[${index}].centreNumber`,
          centreName: `eventNotificationCentres[${index}].centreName`,
        };
        const itemErrors = errors.eventNotificationCentres?.[index] || {};

        const currentArrayItem = getValuesForIndex(index);
        return (
          <React.Fragment key={item._id}>
            <Grid item xs={12} md={3}>
              <Controller
                control={control}
                name={fieldNames.centreNumberType}
                defaultValue={item.centreNumberType}
                rules={{
                  required: true,
                }}
                as={
                  <Select
                    label={
                      fieldLabels["eventNotificationCentres.centreNumberType"]
                    }
                    readOnly={disabled}
                    datatest={`centre-no-type-${index}`}
                    lookupTracker={lookupTracker}
                  >
                    {centreNumberTypeLookups}
                  </Select>
                }
              />
              {itemErrors.centreNumberType && (
                <Error data-test="centre-number-type-error">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                control={control}
                name={fieldNames.centreNumber}
                defaultValue={item.centreNumber}
                rules={{
                  required: currentArrayItem
                    ? currentArrayItem.centreNumberType === NCNTYPEID ||
                    currentArrayItem.centreNumberType === UKPRNTYPEID
                    : false,
                  validate: {
                    ncnLength: (value) =>
                      value && currentArrayItem.centreNumberType === NCNTYPEID
                        ? value.length === 5
                        : true,
                    ukprnLength: (value) =>
                      value && currentArrayItem.centreNumberType === UKPRNTYPEID
                        ? value.length === 8
                        : true,
                  },
                }}
                as={
                  <TextField
                    label={fieldLabels["eventNotificationCentres.centreNumber"]}
                    readOnly={disabled}
                    data-test={`centre-no-${index}`}
                  />
                }
              />
              {itemErrors.centreNumber?.type === "required" && (
                <Error data-test="centre-number-error">
                  This field is required
                </Error>
              )}
              {itemErrors.centreNumber?.type === "ncnLength" && (
                <Error data-test="centre-number-error">
                  NCN centre number must be 5 characters
                </Error>
              )}
              {itemErrors.centreNumber?.type === "ukprnLength" && (
                <Error data-test="centre-number-error">UKPRN centre number must be 8 characters</Error>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                control={control}
                name={fieldNames.centreName}
                defaultValue={item.centreName}
                rules={{
                  required: currentArrayItem
                    ? currentArrayItem.centreNumberType === NATYPEID
                    : false,
                }}
                as={
                  <TextField
                    label={fieldLabels["eventNotificationCentres.centreName"]}
                    readOnly={disabled}
                    data-test={`centre-no-name-${index}`}
                    multiline
                  />
                }
              />
              {itemErrors.centreName && (
                <Error data-test="centre-name-error">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={2}>
              {!disabled ? (
                <Button
                  color="primary"
                  onClick={() => remove(index)}
                  disabled={disabled}
                  data-test="remove-centre"
                >
                  Remove
                </Button>
              ) : null}
            </Grid>
          </React.Fragment>
        );
      })}
      {!disabled ? (
        <Grid item xs={12}>
          <Button
            color="primary"
            onClick={() =>
              append({
                centreNumberType: null,
                centreNumber: null,
                centreName: null,
              })
            }
            disabled={disabled}
            data-test="add-centre"
          >
            Add Centre
          </Button>
        </Grid>
      ) : null}
    </Grid>
  );
};
